import { Transaction } from '@mysten/sui/transactions';
import { SUI_SYSTEM_ADDRESS } from '@mysten/sui/utils';
import { CPC_STAKE_GAS_BUDGET_MIST } from './buildStake.js';
import { CPC_FRAMEWORK, DGC_TYPE } from './constants.js';
import { MAX_FUNGIBLE_REWARD_REDEEMS_PER_TX, planClaimRewardsDrainForFungible, planClaimRewardsDrainForStakedSui, } from './fungibleStakedValue.js';
/** DGC 白皮书 / `staking_pool::LOCK_PERIOD` */
export const CPC_STAKE_LOCK_EPOCHS = 180;
/**
 * Claim/redeem pay gas from AB only. Do not merge loose coins in the same PTB:
 * `suix_getCoins` can return a synthetic CoinReservation of the whole AB; joining
 * that as an input plus empty gas payment makes the node charge AB + 0.1.
 */
function applyClaimAddressBalanceGas(tx) {
    tx.setGasPayment([]);
    tx.setGasBudget(CPC_STAKE_GAS_BUDGET_MIST);
}
function clampRedeemCount(n) {
    if (n == null || !Number.isFinite(n) || n < 1)
        return 1;
    return Math.min(MAX_FUNGIBLE_REWARD_REDEEMS_PER_TX, Math.floor(n));
}
function claimRewardsFromFungible(tx, fss, recipient, redeemCount = 1) {
    const times = clampRedeemCount(redeemCount);
    // Repeat rewards_only: each call only takes this certificate's current slice of
    // pool surplus. One click should match user habit — drain current-epoch yield.
    for (let i = 0; i < times; i++) {
        const rewards = tx.moveCall({
            target: `${SUI_SYSTEM_ADDRESS}::sui_system::redeem_fungible_staked_sui_rewards_only_non_entry`,
            arguments: [tx.object.system(), fss],
        });
        tx.moveCall({
            target: `${CPC_FRAMEWORK}::balance::send_funds`,
            typeArguments: [DGC_TYPE],
            arguments: [rewards, tx.pure.address(recipient)],
        });
    }
    tx.transferObjects([fss], tx.pure.address(recipient));
}
/**
 * Claim staking rewards from an active `StakedSui` receipt.
 * P-UX: rewards → address balance via on-chain entry `send_funds`.
 */
export function buildClaimStakeRewardsFromStakedSui({ stakedSuiId, recipient, redeemCount, }) {
    const tx = new Transaction();
    const [fss] = tx.moveCall({
        target: `${SUI_SYSTEM_ADDRESS}::sui_system::convert_to_fungible_staked_sui`,
        arguments: [tx.object.system(), tx.object(stakedSuiId)],
    });
    claimRewardsFromFungible(tx, fss, recipient, redeemCount);
    applyClaimAddressBalanceGas(tx);
    return tx;
}
/** Claim rewards from an existing `FungibleStakedSui` (already converted). */
export function buildClaimStakeRewardsFromFungible({ fungibleStakedSuiId, recipient, redeemCount, }) {
    const tx = new Transaction();
    const fss = tx.object(fungibleStakedSuiId);
    claimRewardsFromFungible(tx, fss, recipient, redeemCount);
    applyClaimAddressBalanceGas(tx);
    return tx;
}
async function plannedRedeemCount(plan) {
    try {
        const p = await plan;
        if (p == null)
            return 1;
        if (p.redeemCount <= 0) {
            throw new Error('暂无可提收益');
        }
        return clampRedeemCount(p.redeemCount);
    }
    catch (e) {
        if (e instanceof Error && e.message === '暂无可提收益')
            throw e;
        return 1;
    }
}
export async function buildClaimStakeRewardsFromStakedSuiForOwner(client, params) {
    const redeemCount = params.redeemCount ??
        (await plannedRedeemCount(planClaimRewardsDrainForStakedSui(client, params.stakedSuiId)));
    return buildClaimStakeRewardsFromStakedSui({ ...params, redeemCount });
}
export async function buildClaimStakeRewardsFromFungibleForOwner(client, params) {
    const redeemCount = params.redeemCount ??
        (await plannedRedeemCount(planClaimRewardsDrainForFungible(client, params.fungibleStakedSuiId)));
    return buildClaimStakeRewardsFromFungible({ ...params, redeemCount });
}
/**
 * Redeem principal (+ any unlocked rewards) after `lock_until_epoch`.
 * Requires `FungibleStakedSui`; converts from `StakedSui` first if needed (use claim flow).
 */
export function buildRedeemStakePrincipal({ fungibleStakedSuiId, recipient, }) {
    const tx = new Transaction();
    const withdrawn = tx.moveCall({
        target: `${SUI_SYSTEM_ADDRESS}::sui_system::redeem_fungible_staked_sui`,
        arguments: [tx.object.system(), tx.object(fungibleStakedSuiId)],
    });
    tx.moveCall({
        target: `${CPC_FRAMEWORK}::balance::send_funds`,
        typeArguments: [DGC_TYPE],
        arguments: [withdrawn, tx.pure.address(recipient)],
    });
    applyClaimAddressBalanceGas(tx);
    return tx;
}
/** Convert `StakedSui` → `FungibleStakedSui` then redeem principal (fails if still locked). */
export function buildRedeemStakePrincipalFromStakedSui({ stakedSuiId, recipient, }) {
    const tx = new Transaction();
    const [fss] = tx.moveCall({
        target: `${SUI_SYSTEM_ADDRESS}::sui_system::convert_to_fungible_staked_sui`,
        arguments: [tx.object.system(), tx.object(stakedSuiId)],
    });
    const withdrawn = tx.moveCall({
        target: `${SUI_SYSTEM_ADDRESS}::sui_system::redeem_fungible_staked_sui`,
        arguments: [tx.object.system(), fss],
    });
    tx.moveCall({
        target: `${CPC_FRAMEWORK}::balance::send_funds`,
        typeArguments: [DGC_TYPE],
        arguments: [withdrawn, tx.pure.address(recipient)],
    });
    applyClaimAddressBalanceGas(tx);
    return tx;
}
export async function buildRedeemStakePrincipalForOwner(_client, params) {
    return buildRedeemStakePrincipal(params);
}
export async function buildRedeemStakePrincipalFromStakedSuiForOwner(_client, params) {
    return buildRedeemStakePrincipalFromStakedSui(params);
}
